import RegularText from '@/components/RegularText'
import { PAGE_SIZE } from '@/constants'
import { SpecificType } from '@/constants/enum'
import { handleSetDeliveryState } from '@/redux/slice/delivery.slice'
import { handleSetSaveBar } from '@/redux/slice/savebar.slice'
import { IHoliday, ISpecificWorkingDay } from '@/types/components/day'
import { formatDateTime, getWeekdaysFromSpecificWorkingDays, groupDatesByYearMonthDay, removeSpecificDateProcess } from '@/utils'
import { Button, IndexTable, InlineStack } from '@shopify/polaris'
import { DeleteIcon } from '@shopify/polaris-icons'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

const SpecificDateTable = memo(function SpecificDateTable({
  specificDates,
  handleSetSpecificDates,
  handleSetDeliveryWeeklyWorkingDays,
  type = SpecificType.SPECIFIC,
}: {
  specificDates: ISpecificWorkingDay[]
  handleSetSpecificDates: (data: ISpecificWorkingDay[] | IHoliday[]) => void
  handleSetDeliveryWeeklyWorkingDays?: (data: number[]) => void
  type?: SpecificType
}) {
  const { t } = useTranslation('deliverySettings')
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: PAGE_SIZE,
  })
  const dispatch = useDispatch()

  const currentSection = type === SpecificType.SPECIFIC ? 'specificDate' : 'blackoutDate'

  const resourceName = {
    singular: t(`${currentSection}.resourceName.singular`),
    plural: t(`${currentSection}.resourceName.plural`),
  }

  const dateRanges = useMemo(() => {
    const dates = specificDates
      .flatMap((day) => {
        if (!day.dates) return []

        const thisMonthDates = day.dates as unknown as number[]

        return thisMonthDates.map((date) => {
          const currentDate = new Date(day.year, day.month - 1, Number(date))
          return currentDate
        })
      })
      .filter((date, index, self) => date && self.indexOf(date) === index) // Lọc ra các giá trị undefined/null
      .sort((a, b) => a.getTime() - b.getTime()) // Sắp xếp các ngày theo thứ tự thời gian

    if (dates.length === 0) return []

    const groupedRanges = []
    let start = dates[0]
    let end = dates[0]

    for (let i = 1; i < dates.length; i++) {
      const currentDate = dates[i]
      const previousDate = dates[i - 1]

      // Kiểm tra xem ngày hiện tại có liền kề với ngày trước không
      if (currentDate.getTime() - previousDate.getTime() === 24 * 60 * 60 * 1000) {
        end = currentDate
      } else {
        // Nếu không liền kề, đẩy khoảng thời gian vào mảng groupedRanges
        groupedRanges.push({
          start,
          end,
        })
        // Bắt đầu một khoảng thời gian mới
        start = currentDate
        end = currentDate
      }
    }

    // Đẩy khoảng thời gian cuối cùng
    groupedRanges.push({
      start,
      end,
    })

    return groupedRanges
  }, [specificDates])

  const handleDelete = (item: { start: Date; end: Date }) => {
    dispatch(handleSetSaveBar({ isCheckSave: false }))
    if (type === SpecificType.SPECIFIC) {
      dispatch(handleSetDeliveryState({
        isSaving: false
      }))
    }
    const groupedDates = groupDatesByYearMonthDay(item)
    const mergedSpecificDays = removeSpecificDateProcess(groupedDates, specificDates)

    handleSetSpecificDates(mergedSpecificDays)

    if (type === SpecificType.BLACKOUT) {
      return
    }

    const weekdaysFromSpecificDates = getWeekdaysFromSpecificWorkingDays(mergedSpecificDays as unknown as ISpecificWorkingDay[])
    handleSetDeliveryWeeklyWorkingDays && handleSetDeliveryWeeklyWorkingDays(weekdaysFromSpecificDates)
  }

  const currentPageDates = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage
    const end = start + pagination.perPage

    return dateRanges.slice(start, end) ? dateRanges.slice(start, end) : dateRanges
  }, [dateRanges, pagination])

  useEffect(() => {
    if (currentPageDates.length === 0 && dateRanges.length > 0) {
      setPagination((prev) => ({
        ...prev,
        page: Math.ceil(dateRanges.length / prev.perPage),
      }))
    }
  }, [currentPageDates, dateRanges.length])

  if (dateRanges.length === 0) {
    return null
  }

  return (
    <IndexTable
      resourceName={resourceName}
      itemCount={dateRanges.length}
      selectable={false}
      emptyState={undefined}
      headings={[{ title: t(`${currentSection}.table.heading.dates`) }, { title: '', alignment: 'end' }]}
      pagination={
        dateRanges.length > pagination.perPage
          ? {
            hasNext: dateRanges.length > pagination.page * pagination.perPage,
            onNext: () => {
              setPagination((prev) => ({
                ...prev,
                page: prev.page + 1,
              }))
            },
            hasPrevious: pagination.page > 1,
            onPrevious: () => {
              setPagination((prev) => ({
                ...prev,
                page: prev.page - 1,
              }))
            },
            label: `${pagination.page} / ${Math.ceil(dateRanges.length / pagination.perPage)}`,
          }
          : undefined
      }
    >
      {currentPageDates.map(({ start, end }, index: number) => (
        <IndexTable.Row id={index.toString()} position={index} key={`time-slot--${index}`}>
          <IndexTable.Cell>
            <RegularText>
              {start.getTime() === end.getTime()
                ? formatDateTime(start)
                : `${formatDateTime(start)} - ${formatDateTime(end)}`}
            </RegularText>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <InlineStack align="end">
              <Button variant="plain" icon={DeleteIcon} tone="critical" onClick={() => handleDelete({ start, end })} />
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      ))}
    </IndexTable>
  )
})

export default SpecificDateTable
